import { financialProtectionErrorMessage } from '../../shared/models/financial-protection';
import { Component, DestroyRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { AssignEmployeeService } from '../../services/assign-employee.service';
import {filter, finalize, map, switchMap, take, tap} from 'rxjs/operators';
import { EmployeeAssignStep } from '../../shared/assign-employee/employee-assign-step';
import { Router } from '@angular/router';
import { PhaseWithEmployees } from '../../projects/domain/phase-with-employees';
import { ProjectService } from '../../services/project.service';
import { NotificationService } from '../../../core/notification.service';
import {PhaseAndEmployees} from "../../projects/domain/phase-and-employees";
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { PhasesStepComponent } from '../../shared/assign-employee/phases-step/phases-step.component';

@Component({
  selector: 'app-assign-employee',

  standalone: false,
  template: `
    <ng-container [ngSwitch]="step$.asObservable() | async">
      <app-projects-step
        *ngSwitchCase="'projects'"
        (closeEmployeeAssign)="onCloseEmployeeAssign()"
        (projectSelected)="onProjectSelected($event)"
      ></app-projects-step>
      <ng-container *ngIf="project$ | async as project">
        <app-phases-step
          *ngSwitchCase="'phases'"
          [isPreviousStep]="true"
          [isNextStep]="false"
          [projectId]="project.id"
          [phases]="project.phases"
          [employeeIds]="employeeIds$ | async"
          [phasesWithEmployees]="phasesWithEmployees$ | async"
          (closeEmployeeAssign)="onCloseEmployeeAssign()"
          (backToPreviousStep)="setStep('projects')"
          (phaseSelected)="onPhaseSelected($event, project.phases)"
        ></app-phases-step>
      </ng-container>
    </ng-container>
  `,
})
export class AssignEmployeeFromEmployeesComponent {
  @ViewChild(PhasesStepComponent) private phasesStep?: PhasesStepComponent;
  private assignmentPending = false;
  step$ = new BehaviorSubject<EmployeeAssignStep>('projects');

  project$ = this.assignEmployeeService.project$;
  phase$ = this.assignEmployeeService.phase$;
  employeeIds$ = this.assignEmployeeService.employeeIds$;
  phasesWithEmployees$ = new BehaviorSubject<PhaseWithEmployees[]>([]);
  constructor(
    private readonly assignEmployeeService: AssignEmployeeService,
    private readonly router: Router,
    private readonly projectService: ProjectService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly destroyRef: DestroyRef,
  ) {}

  setStep(value: EmployeeAssignStep): void {
    this.step$.next(value);
  }

  onCloseEmployeeAssign(): void {
    this.router.navigate(['/main/employees']);
  }

  onProjectSelected(projectId: string): void {
    this.projectService.getProject(projectId)
      .pipe(
        take(1),
        tap((project) => this.assignEmployeeService.setProject(project)),
        switchMap(() => this.projectService.getProjectPhasesWithEmployees(projectId))
      )
      .subscribe((phasesWithEmployees: PhaseWithEmployees[]) => {
        this.phasesWithEmployees$.next(phasesWithEmployees);
        this.setStep('phases');
      });
  }

  onPhaseSelected(phase: PhaseWithEmployees, otherPhases: PhaseAndEmployees[]): void {
    const matchingPhase = otherPhases.find(p => p.id === phase.id);

    if (matchingPhase && matchingPhase.requiredProfessions) {
      phase.requiredProfessions = matchingPhase.requiredProfessions;
    }

    this.assignEmployeeService.setPhase(phase);

    combineLatest([this.project$, this.phase$, this.employeeIds$])
      .pipe(
        take(1),
        map(([project, phase, employeeIds]): [string, PhaseWithEmployees, string[]] =>
          [project.id, phase, [...employeeIds]]
        ),
      )
      .subscribe(([projectId, phase, employeeIds]) =>
        this.assignEmployeeToPhase(projectId, phase, employeeIds)
      );
  }

  private assignEmployeeToPhase(projectId: string, phase: PhaseWithEmployees, employeeIds: string[]) {
    if (this.assignmentPending) return;
    this.assignmentPending = true;
    const requests = employeeIds.map(employeeId => ({ employeeId }));
    const currentSelection = () => combineLatest([this.project$, this.phase$, this.employeeIds$]).pipe(
      take(1),
      filter(([project, selectedPhase, selectedIds]) => this.step$.value === 'phases'
        && this.phasesStep?.form.controls.phase.value?.id === phase.id
        && project?.id === projectId && selectedPhase?.id === phase.id
        && selectedIds.length === employeeIds.length && employeeIds.every(id => selectedIds.includes(id))),
    );
    this.assignEmployeeService.previewAssignmentDates(projectId, phase.id, requests).pipe(
      switchMap(preview => currentSelection().pipe(switchMap(() => {
        let confirmation$ = of(true);
        if (preview.warnings.length) {
          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '550px',
            data: { title: 'Review work dates', message: `${preview.warnings.join(' ')} Do you want to assign the selected employee(s)?`, confirmLabel: 'Assign', color: 'primary' },
            autoFocus: false,
            restoreFocus: false,
          });
          confirmation$ = dialogRef.afterClosed().pipe(filter(result => result === true), finalize(() => dialogRef.close()));
        }
        return confirmation$.pipe(
          switchMap(() => currentSelection()),
          map(() => preview),
        );
      }))),
      takeUntilDestroyed(this.destroyRef),
      switchMap(preview => this.assignEmployeeService.assignEmployeeToPhase(employeeIds, projectId, phase.id).pipe(
        map(phases => ({ phases, before: preview.phase })),
      )),
      finalize(() => { this.assignmentPending = false; }),
    ).subscribe({ next: ({ phases, before }) => {
        this.projectService.refreshProject(projectId);
        const result = this.assignEmployeeService.assignmentDateResult(before, phases, phase.id, requests);
        this.notificationService.setNotification(`Employee assigned.${result.message}`, '', result.adjusted || result.unverified ? { severity: 'warning' } : { duration: 15000 });
        this.onCloseEmployeeAssign();
      }, error: error => {
        const financialMessage = financialProtectionErrorMessage(error, undefined, employeeIds.length > 1 ? 'bulk' : 'single');
        const savedDatesUnavailable = error.message === 'The employees were assigned, but we couldn’t display their saved work dates. Refresh the project to check the assignments. Do not submit them again.';
        this.notificationService.setNotification(financialMessage || error?.error?.msg || error.message || 'Failed to assign employee.', '', { severity: savedDatesUnavailable || error?.error?.assignedCount > 0 ? 'warning' : 'error' });
        if (error?.error?.assignedCount > 0) this.projectService.refreshProject(projectId);
      } });
  }
}
